/**
 * PAYMENT METHOD SERVICE
 * Centralized service for managing all payment methods and their calculations
 * Supports detailed breakdown of electronic payment methods
 */

import { normalizePaymentMethodForSession, isCreditSale } from '../utils/saleSessionHelpers';
import { resolveSaleRegisterIncome } from '../domain/sales/checkout/paymentAllocation';
import { isSaleFullyCancelled } from '../domain/sales/storno/saleVoidPlanning';

// Payment method definitions with icons, colors, and translations
export const PAYMENT_METHODS = {
  cash: {
    id: 'cash',
    name: { ar: 'نقداً', fr: 'Espèces', en: 'Cash' },
    icon: 'Banknote',
    color: 'var(--ads-color-success)',
    category: 'physical'
  },
  bankily: {
    id: 'bankily',
    name: { ar: 'بنكيلي', fr: 'Bankily', en: 'Bankily' },
    icon: 'CreditCard',
    color: 'var(--ads-color-primary)',
    category: 'electronic'
  },
  massrivi: {
    id: 'massrivi',
    name: { ar: 'مصرفي', fr: 'Massrivi', en: 'Massrivi' },
    icon: 'CreditCard',
    color: 'var(--ads-color-info)',
    category: 'electronic'
  },
  'bamis-digital': {
    id: 'bamis-digital',
    name: { ar: 'باميس الرقمي', fr: 'Bamis Digital', en: 'Bamis Digital' },
    icon: 'Smartphone',
    color: 'var(--ads-color-primary-active)',
    category: 'electronic'
  },
  click: {
    id: 'click',
    name: { ar: 'كليك', fr: 'Click', en: 'Click' },
    icon: 'Smartphone',
    color: 'var(--ads-color-danger)',
    category: 'electronic'
  },
  'moov-money': {
    id: 'moov-money',
    name: { ar: 'موف موني', fr: 'Moov Money', en: 'Moov Money' },
    icon: 'Smartphone',
    color: 'var(--ads-color-warning)',
    category: 'electronic'
  },
  amanti: {
    id: 'amanti',
    name: { ar: 'أمانتي', fr: 'Amanti', en: 'Amanti' },
    icon: 'Smartphone',
    color: 'var(--ads-color-info-dark)',
    category: 'electronic'
  },
  'gaza-pay': {
    id: 'gaza-pay',
    name: { ar: 'غزة باي', fr: 'Gaza Pay', en: 'Gaza Pay' },
    icon: 'Smartphone',
    color: 'var(--ads-color-success-dark)',
    category: 'electronic'
  },
  'rasidi': {
    id: 'rasidi',
    name: { ar: 'رصيدي', fr: 'Rasidi', en: 'Rasidi' },
    icon: 'CreditCard',
    color: 'var(--ads-color-primary)',
    category: 'electronic'
  },
  'bim-bank': {
    id: 'bim-bank',
    name: { ar: 'بيم بنك', fr: 'BIM Bank', en: 'BIM Bank' },
    icon: 'CreditCard',
    color: 'var(--ads-color-info-dark)',
    category: 'electronic'
  },
  'bci-pay': {
    id: 'bci-pay',
    name: { ar: 'بي سي آي باي', fr: 'BCI Pay', en: 'BCI Pay' },
    icon: 'CreditCard',
    color: 'var(--ads-color-primary-active)',
    category: 'electronic'
  },
  'sedad': {
    id: 'sedad',
    name: { ar: 'سداد', fr: 'Sedad', en: 'Sedad' },
    icon: 'Smartphone',
    color: 'var(--ads-color-danger)',
    category: 'electronic'
  },
};

/** Map a sale to a PAYMENT_METHODS key (cash, bankily, …). */
export function resolveSalePaymentMethodKey(sale) {
  const normalized = normalizePaymentMethodForSession(sale);
  if (normalized === 'cash') return 'cash';
  if (PAYMENT_METHODS[normalized]) return normalized;
  if (['electronic', 'électronique', 'card', 'mobile'].includes(normalized)) {
    return 'bankily';
  }
  return normalized;
}

// Payment method categories
export const PAYMENT_CATEGORIES = {
  physical: {
    name: { ar: 'نقدي', fr: 'Physique', en: 'Physical' },
    color: 'var(--ads-color-success)',
    icon: 'Banknote'
  },
  electronic: {
    name: { ar: 'إلكتروني', fr: 'Électronique', en: 'Electronic' },
    color: 'var(--ads-color-primary)',
    icon: 'CreditCard'
  }
};

/**
 * Calculate payment method totals from sales data.
 * Uses register income (paid now) — not full sale total / créance.
 * Fully cancelled sales are excluded.
 */
export const calculatePaymentTotals = (sales, language = 'fr') => {
  const totals = {};
  const categories = { physical: 0, electronic: 0 };
  
  // Initialize all payment methods
  Object.keys(PAYMENT_METHODS).forEach(method => {
    totals[method] = {
      amount: 0,
      count: 0,
      percentage: 0
    };
  });
  
  let countedSales = 0;
  (Array.isArray(sales) ? sales : []).forEach((sale) => {
    if (isSaleFullyCancelled(sale)) return;
    const { amount } = resolveSaleRegisterIncome(sale);
    const numericAmount = Number(amount) || 0;
    if (numericAmount <= 0) return;

    countedSales += 1;
    const methodKey = resolveSalePaymentMethodKey(sale);

    if (PAYMENT_METHODS[methodKey]) {
      totals[methodKey].amount += numericAmount;
      totals[methodKey].count += 1;
      const category = PAYMENT_METHODS[methodKey].category;
      categories[category] += numericAmount;
      return;
    }

    if (methodKey !== 'cash' && methodKey !== 'credit') {
      if (!totals[methodKey]) {
        totals[methodKey] = { amount: 0, count: 0, percentage: 0 };
      }
      totals[methodKey].amount += numericAmount;
      totals[methodKey].count += 1;
      categories.electronic += numericAmount;
    }
  });
  
  // Calculate percentages
  const totalAmount = Object.values(totals).reduce((sum, method) => sum + method.amount, 0);
  Object.keys(totals).forEach(method => {
    totals[method].percentage = totalAmount > 0 ? (totals[method].amount / totalAmount) * 100 : 0;
  });
  
  return {
    methods: totals,
    categories,
    totalAmount,
    totalCount: countedSales
  };
};

/**
 * Get payment method display name
 * @param {string} methodId - Payment method ID
 * @param {string} language - Current language
 * @returns {string} Display name
 */
export const getPaymentMethodName = (methodId, language = 'fr') => {
  const method = PAYMENT_METHODS[methodId];
  if (!method) return methodId;
  return method.name[language] || method.name.fr;
};

/**
 * Get payment method icon
 * @param {string} methodId - Payment method ID
 * @returns {string} Icon name
 */
export const getPaymentMethodIcon = (methodId) => {
  const method = PAYMENT_METHODS[methodId];
  return method ? method.icon : 'CreditCard';
};

/**
 * Get payment method color
 * @param {string} methodId - Payment method ID
 * @returns {string} Color value
 */
export const getPaymentMethodColor = (methodId) => {
  const method = PAYMENT_METHODS[methodId];
  return method ? method.color : 'var(--ads-color-primary)';
};

/**
 * Get payment methods by category
 * @param {string} category - Category (physical, electronic)
 * @returns {Array} Array of payment methods
 */
export const getPaymentMethodsByCategory = (category) => {
  return Object.values(PAYMENT_METHODS).filter(method => method.category === category);
};

/**
 * Get all electronic payment methods
 * @returns {Array} Array of electronic payment methods
 */
export const getElectronicPaymentMethods = () => {
  return getPaymentMethodsByCategory('electronic');
};

/**
 * Get all physical payment methods
 * @returns {Array} Array of physical payment methods
 */
export const getPhysicalPaymentMethods = () => {
  return getPaymentMethodsByCategory('physical');
};

/** Options pour paiements fournisseurs / dépenses — une entrée par anbieter (pas « électronique » générique). */
export function getOutflowPaymentMethodOptions(language = 'fr') {
  return Object.values(PAYMENT_METHODS).map((method) => ({
    value: method.id,
    label: method.name[language] || method.name.fr,
    category: method.category,
    icon: method.icon,
    color: method.color
  }));
}

export function isValidOutflowPaymentMethod(methodId) {
  const key = String(methodId || '').toLowerCase();
  return key === 'cash' || Boolean(PAYMENT_METHODS[key]);
}

/**
 * Filter sales by payment method
 * @param {Array} sales - Array of sales data
 * @param {string} paymentMethod - Payment method to filter by
 * @returns {Array} Filtered sales
 */
export const filterSalesByPaymentMethod = (sales, paymentMethod) => {
  if (paymentMethod === 'all') return sales;

  return sales.filter((sale) => {
    if (paymentMethod === 'credit') {
      return isCreditSale(sale);
    }

    // Créance ≠ electronic/physical cash register income
    if (isCreditSale(sale)) {
      return false;
    }

    const salePaymentMethod = (sale.paymentMethod || 'cash').toLowerCase();

    if (paymentMethod === 'electronic') {
      return salePaymentMethod !== 'cash' && salePaymentMethod !== 'espèces';
    }

    if (paymentMethod === 'physical') {
      return salePaymentMethod === 'cash' || salePaymentMethod === 'espèces';
    }

    if (salePaymentMethod === 'electronic' || salePaymentMethod === 'électronique') {
      const electronicMethod = sale.electronicPaymentMethod || sale.electronicMethod;
      return electronicMethod && electronicMethod.toLowerCase() === paymentMethod;
    }

    return salePaymentMethod === paymentMethod;
  });
};

/**
 * Get payment method statistics
 * @param {Array} sales - Array of sales data
 * @param {string} language - Current language
 * @returns {Object} Statistics object
 */
export const getPaymentMethodStatistics = (sales, language = 'fr') => {
  const totals = calculatePaymentTotals(sales, language);
  const stats = {
    totalAmount: totals.totalAmount,
    totalCount: totals.totalCount,
    methodCount: Object.keys(PAYMENT_METHODS).length,
    topMethod: null,
    topMethodAmount: 0,
    methods: []
  };
  
  // Find top method
  Object.entries(totals.methods).forEach(([methodId, data]) => {
    if (data.amount > 0) {
      stats.methods.push({
        id: methodId,
        name: getPaymentMethodName(methodId, language),
        icon: getPaymentMethodIcon(methodId),
        color: getPaymentMethodColor(methodId),
        amount: data.amount,
        count: data.count,
        percentage: data.percentage
      });
      
      if (data.amount > stats.topMethodAmount) {
        stats.topMethod = methodId;
        stats.topMethodAmount = data.amount;
      }
    }
  });
  
  // Sort methods by amount (descending)
  stats.methods.sort((a, b) => b.amount - a.amount);
  
  return stats;
};
